import { Component } from '@theme/component';

/**
 * @typedef {Object} QuoteCarouselRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} content
 *
 * @extends {Component<QuoteCarouselRefs>}
 */
class QuoteCarouselComponent extends Component {
  requiredRefs = ['track', 'content'];
  #mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #isPointerInside = false;
  #isFocusInside = false;
  #abortController = new AbortController();

  #resizeObserver = new ResizeObserver(() => {
    this.#syncTrack();
  });

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;
    this.#resizeObserver.observe(this);
    this.addEventListener('focusin', () => this.#handleFocusIn(), { signal });
    this.addEventListener('focusout', (e) => this.#handleFocusOut(/** @type {FocusEvent} */(e)), { signal });
    this.#mediaQuery.addEventListener('change', () => this.#syncMotionPreference(), { signal });
    this.#syncMotionPreference();
    this.#syncTrack();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncTrack();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#abortController.abort();
  }

  pause = () => {
    this.dataset.paused = 'true';
  };

  resume = () => {
    this.#resumeIfAllowed();
  };

  handlePointerEnter = () => {
    this.#isPointerInside = true;
    this.pause();
  };

  handlePointerLeave = () => {
    this.#isPointerInside = false;
    this.#resumeIfAllowed();
  };

  #handleFocusIn() {
    this.#isFocusInside = true;
    this.pause();
  }

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut(event) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && this.contains(relatedTarget)) return;

    this.#isFocusInside = false;
    this.#resumeIfAllowed();
  }

  #syncMotionPreference() {
    this.dataset.reducedMotion = String(this.#prefersReducedMotion());

    if (this.#prefersReducedMotion()) {
      this.dataset.paused = 'true';
    } else if (!this.#isPointerInside && !this.#isFocusInside) {
      this.dataset.paused = 'false';
    }
  }

  #resumeIfAllowed() {
    if (this.#isPointerInside || this.#isFocusInside || this.#prefersReducedMotion()) {
      this.pause();
      return;
    }

    this.dataset.paused = 'false';
  }

  #prefersReducedMotion() {
    return this.#mediaQuery.matches;
  }

  #syncTrack() {
    const track = this.refs.track;
    const content = track instanceof HTMLElement ? track.querySelector(':scope > [ref="content"]') : null;

    if (!(track instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    track.querySelectorAll('[data-duplicate="true"]').forEach((element) => element.remove());

    const contentWidth = content.getBoundingClientRect().width;
    const containerWidth = this.getBoundingClientRect().width;
    const speed = Number(this.dataset.speed) || 40;
    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;

    if (!contentWidth || !containerWidth) {
      this.dataset.ready = 'false';
      return;
    }

    let totalWidth = contentWidth;
    const requiredWidth = containerWidth + contentWidth + gap;
    let duplicateCount = 0;

    while (totalWidth < requiredWidth && duplicateCount < 12) {
      const duplicate = content.cloneNode(true);

      if (!(duplicate instanceof HTMLElement)) break;

      duplicate.removeAttribute('ref');
      duplicate.dataset.duplicate = 'true';
      duplicate.setAttribute('aria-hidden', 'true');
      duplicate.querySelectorAll('[ref]').forEach((element) => element.removeAttribute('ref'));
      track.append(duplicate);
      totalWidth += contentWidth + gap;
      duplicateCount += 1;
    }

    this.style.setProperty('--quote-carousel-distance', `${contentWidth + gap}px`);
    this.style.setProperty('--quote-carousel-duration', `${Math.max((contentWidth + gap) / speed, 12)}s`);
    this.dataset.ready = 'true';
  }
}

if (!customElements.get('quote-carousel-component')) {
  customElements.define('quote-carousel-component', QuoteCarouselComponent);
}